import { AcademicLabels } from "../../modules/academic-curriculum/academic.labels";
import { BudgetLabels } from "../../modules/student-engagement/budget/budget.labels";
import { Proto } from "../../pojo/Proto";
import URLString = Proto.URLString;
import { S25Const } from "../../util/s25-const";
import { S25Util } from "../../util/s25-util";
import { matchKeyAndProp } from "../../pojo/Util";

type AbstractState<N extends string = string, T extends Record<string, unknown> = Record<string, unknown>> = {
    readonly name: N;
    readonly url: URLString;
    readonly parent: "appRoot" | "external" | keyof T;
    readonly views: any;
    readonly component?: string;
    readonly display?: string;
    readonly root?: boolean;
    readonly viewPrefName?: string;
    readonly sort?: number;
    readonly isDefault?: boolean;
    readonly sticky?: boolean;
    readonly params?: { [key: string]: unknown };
    readonly deepRedirect?: boolean;
    readonly isHidden?: boolean;
    /** Forwarded to ui-router. Set false to keep a state from reloading when only the query string changes. */
    readonly reloadOnSearch?: boolean;
};

const matchName = matchKeyAndProp("name");
const states = matchName({
    home: {
        name: "home",
        component: "home",
        url: "/home",
        root: true,
        parent: "appRoot", //defined in framework.js
        views: {
            "": "home",
        },
        viewPrefName: "25L_home_mode",
    },
    dash: {
        name: "dash",
        url: "/dash",
        display: "25Live",
        sort: 1,
        isDefault: true,
        parent: "home",
        views: {
            dash: "homeDash",
        },
        sticky: true,
    },
    chat: {
        name: "chat",
        component: "chat",
        // Deep-link query params for email links. Declared in the url string so ui-router parses them.
        // Default reloadOnSearch (true): changing ?externalId etc. remounts chat so s25-chat re-inits
        // the iframe (ngOnInit only). Do not set reloadOnSearch:false — unlike seEngagementAnalytics,
        // chat does not sync filters back into the URL.
        url: "/chat?externalId&channelType&socChannelId&title&externalType",
        display: "Chat",
        sort: 1.05,
        parent: "home",
        views: {
            chat: "chat",
        },
        sticky: true,
    },
    productFeedback: {
        name: "productFeedback",
        url: "/product/feedback",
        display: "Product Feedback",
        sort: 1.06,
        parent: "home",
        isHidden: true,
        views: {
            productFeedback: "productFeedback",
        },
        sticky: true,
    },
    paymentCancel: {
        name: "paymentCancel",
        url: "/payment/cancel",
        display: "Payment Cancel",
        sort: 1.3,
        parent: "home",
        views: {
            paymentCancel: "paymentCancel",
        },
    },
    paymentSuccess: {
        name: "paymentSuccess",
        url: "/payment/success/:sessionId",
        display: "Payment Success",
        sort: 1.4,
        parent: "home",
        views: {
            paymentSuccess: "paymentSuccess",
        },
        params: {
            sessionId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
    },
    passwordReset: {
        name: "passwordReset",
        url: "/password_reset?pw_reset_token",
        parent: "home",
        isHidden: true,
        views: {
            passwordReset: "passwordReset",
        },
    },
    paymentPortal: {
        name: "paymentPortal",
        component: "paymentPortal",
        url: "/payment/portal/:id",
        display: "Payment Portal",
        sort: 0.01,
        parent: "appRoot",
        views: {
            "": "paymentPortal",
        },
        params: {
            id: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
    },
    bulkEdit: {
        name: "bulkEdit",
        url: "/bulk/edit/:search/:cacheId/:itemTypeId/:dateView",
        display: "Bulk Edit",
        sort: 12,
        parent: "home",
        views: {
            bulkEdit: "bulkEdit",
        },
        sticky: true,
    },
    rose: {
        name: "rose",
        url: "/event/form",
        params: {},
        display: "Event Form",
        sort: 1.1,
        parent: "home",
        views: {
            rose: "homeRose",
        },
        sticky: true,
    },
    systemSettings: {
        name: "systemSettings",
        url: "/system/settings",
        display: "System Settings",
        sort: 1.2,
        parent: "home",
        views: {
            systemSettings: "systemSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    bpeSettings: {
        name: "bpeSettings",
        url: "/bpe",
        display: "Event Save Email",
        sort: 1.22,
        parent: "systemSettings",
        views: {
            bpeSettings: "bpeSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    bpeScenarios: {
        name: "bpeScenarios",
        url: "/scenarios",
        display: "Scenario Settings",
        sort: 1.221,
        parent: "bpeSettings",
        views: {
            bpeScenarios: "bpeScenarios",
        },
        sticky: true,
        deepRedirect: true,
    },
    bpeTemplates: {
        name: "bpeTemplates",
        url: "/templates",
        display: "Template Settings",
        sort: 1.222,
        parent: "bpeSettings",
        views: {
            bpeTemplates: "bpeTemplates",
        },
        sticky: true,
        deepRedirect: true,
    },
    bpeTemplatesEmail: {
        name: "bpeTemplatesEmail",
        url: "/email",
        display: "Emails",
        sort: 1.2221,
        parent: "bpeTemplates",
        views: {
            bpeTemplatesEmail: "bpeTemplatesEmail",
        },
        sticky: true,
        deepRedirect: true,
    },
    bpeTemplatesTodo: {
        name: "bpeTemplatesTodo",
        url: "/todo",
        display: "To Dos",
        sort: 1.2222,
        parent: "bpeTemplates",
        views: {
            bpeTemplatesTodo: "bpeTemplatesTodo",
        },
        sticky: true,
        deepRedirect: true,
    },
    generalSystemSettings: {
        name: "generalSystemSettings",
        url: "/general",
        display: "General Settings",
        sort: 1.21,
        isDefault: true,
        parent: "systemSettings",
        views: {
            generalSystemSettings: "generalSystemSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    masqueradeSettings: {
        name: "masqueradeSettings",
        url: "/masquerade",
        display: "Masquerade",
        sort: 1.211,
        parent: "generalSystemSettings",
        views: {
            masqueradeSettings: "masqueradeSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    masqueradeSwitch: {
        name: "masqueradeSwitch",
        url: "/switch",
        display: "Switch Users",
        sort: 1.2111,
        parent: "masqueradeSettings",
        views: {
            masqueradeSwitch: "masqueradeSwitch",
        },
        sticky: true,
        deepRedirect: true,
    },
    masqueradeAdmin: {
        name: "masqueradeAdmin",
        url: "/admin",
        display: "Administration",
        sort: 1.2112,
        parent: "masqueradeSettings",
        views: {
            masqueradeAdmin: "masqueradeAdmin",
        },
        sticky: true,
        deepRedirect: true,
    },
    theme: {
        name: "theme",
        url: "/theme",
        display: "Configure Theme",
        sort: 1.212,
        parent: "generalSystemSettings",
        views: {
            theme: "theme",
        },
        sticky: true,
        deepRedirect: true,
    },
    gridColoring: {
        name: "gridColoring",
        url: "/gridColoring",
        display: "Grid Coloring",
        sort: 1.213,
        parent: "generalSystemSettings",
        views: {
            gridColoring: "gridColoring",
        },
        sticky: true,
        deepRedirect: true,
    },
    gridColoringMapping: {
        name: "gridColoringMapping",
        url: "/mapping",
        display: "Color Mapping",
        sort: 1.2131,
        parent: "gridColoring",
        views: {
            gridColoringMapping: "gridColoringMapping",
        },
        sticky: true,
        deepRedirect: true,
    },
    gridColoringSchemes: {
        name: "gridColoringSchemes",
        url: "/schemes",
        display: "Color Schemes",
        sort: 1.2132,
        parent: "gridColoring",
        views: {
            gridColoringSchemes: "gridColoringSchemes",
        },
        sticky: true,
        deepRedirect: true,
    },
    userActivity: {
        name: "userActivity",
        url: "/activity",
        display: "User Activity",
        sort: 1.214,
        parent: "generalSystemSettings",
        views: {
            userActivity: "userActivity",
        },
        sticky: true,
        deepRedirect: true,
    },
    lockedItems: {
        name: "lockedItems",
        url: "/locked",
        display: "Locked Items",
        sort: 1.2141,
        parent: "userActivity",
        views: {
            lockedItems: "lockedItems",
        },
        sticky: true,
        deepRedirect: true,
    },
    pendingLocations: {
        name: "pendingLocations",
        url: "/pending/locations",
        display: "Pending Locations",
        sort: 1.2142,
        parent: "userActivity",
        views: {
            pendingLocations: "pendingLocations",
        },
        sticky: true,
        deepRedirect: true,
    },
    pendingResources: {
        name: "pendingResources",
        url: "/pending/resources",
        display: "Pending Resources",
        sort: 1.2143,
        parent: "userActivity",
        views: {
            pendingResources: "pendingResources",
        },
        sticky: true,
        deepRedirect: true,
    },
    signedInUsers: {
        name: "signedInUsers",
        url: "/signedIn",
        display: "Signed-in Users",
        sort: 1.2144,
        parent: "userActivity",
        views: {
            signedInUsers: "signedInUsers",
        },
        sticky: true,
        deepRedirect: true,
    },
    emailDefaults: {
        name: "emailDefaults",
        url: "/email",
        display: "Email Defaults",
        sort: 1.215,
        parent: "generalSystemSettings",
        views: {
            emailDefaults: "emailDefaults",
        },
        sticky: true,
        deepRedirect: true,
    },
    systemDefaults: {
        name: "systemDefaults",
        url: "/system",
        display: "System Defaults",
        sort: 1.216,
        parent: "generalSystemSettings",
        views: {
            systemDefaults: "systemDefaults",
        },
        sticky: true,
        deepRedirect: true,
    },
    userDefaults: {
        name: "userDefaults",
        url: "/user",
        display: "User Defaults",
        sort: 1.217,
        parent: "generalSystemSettings",
        views: {
            userDefaults: "userDefaults",
        },
        sticky: true,
        deepRedirect: true,
    },
    dateTimeDefaults: {
        name: "dateTimeDefaults",
        url: "/dateTime",
        display: "Date & Time Defaults",
        sort: 1.218,
        parent: "generalSystemSettings",
        views: {
            dateTimeDefaults: "dateTimeDefaults",
        },
        sticky: true,
        deepRedirect: true,
    },
    roseSettings: {
        name: "roseSettings",
        url: "/event/form",
        display: "Event Form Settings",
        sort: 1.22,
        parent: "systemSettings",
        views: {
            roseSettings: "roseSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    formConfig: {
        name: "formConfig",
        url: "/config",
        display: "Config Settings",
        sort: 1.221,
        parent: "roseSettings",
        views: {
            formConfig: "formConfig",
        },
        sticky: true,
        deepRedirect: true,
    },
    formRules: {
        name: "formRules",
        url: "/rules",
        display: "Rule Settings",
        sort: 1.222,
        parent: "roseSettings",
        views: {
            formRules: "formRules",
        },
        sticky: true,
        deepRedirect: true,
    },
    actionRules: {
        name: "actionRules",
        url: "/action",
        display: "Actions",
        sort: 1.2221,
        parent: "formRules",
        views: {
            actionRules: "actionRules",
        },
        sticky: true,
        deepRedirect: true,
    },
    validationRules: {
        name: "validationRules",
        url: "/validation",
        display: "Validation",
        sort: 1.2222,
        parent: "formRules",
        views: {
            validationRules: "validationRules",
        },
        sticky: true,
        deepRedirect: true,
    },
    formDefaults: {
        name: "formDefaults",
        url: "/defaults",
        display: "Default Settings",
        sort: 1.223,
        parent: "roseSettings",
        views: {
            formDefaults: "formDefaults",
        },
        sticky: true,
        deepRedirect: true,
    },
    eventQuotas: {
        name: "eventQuotas",
        url: "/quotas",
        display: "Event Quotas",
        sort: 1.224,
        parent: "roseSettings",
        views: {
            eventQuotas: "eventQuotas",
        },
        sticky: true,
        deepRedirect: true,
    },
    embeddingConfig: {
        name: "embeddingConfig",
        url: "/embedding",
        display: "Embedding",
        sort: 1.24,
        parent: "systemSettings",
        views: {
            embeddingConfig: "embeddingConfig",
        },
        sticky: true,
    },
    blackoutManagement: {
        name: "blackoutManagement",
        url: "/blackout/management",
        display: "Blackout Management",
        sort: 1.251,
        parent: "systemSettings",
        views: {
            blackoutManagement: "blackoutManagement",
        },
        sticky: true,
    },
    specialDateManagement: {
        name: "specialDateManagement",
        url: "/special/dates",
        display: "Special Dates",
        sort: 1.2515,
        parent: "systemSettings",
        views: {
            specialDateManagement: "specialDateManagement",
        },
        sticky: true,
        deepRedirect: true,
    },
    specialDateList: {
        name: "specialDateList",
        url: "/list",
        display: "Special Date",
        sort: 1.25151,
        parent: "specialDateManagement",
        views: {
            specialDateList: "specialDateList",
        },
        sticky: true,
    },
    specialDateBulkUpload: {
        name: "specialDateBulkUpload",
        url: "/bulk/upload",
        display: "Bulk Upload",
        sort: 1.25152,
        parent: "specialDateManagement",
        views: {
            specialDateBulkUpload: "specialDateBulkUpload",
        },
        sticky: true,
    },
    documentManagement: {
        name: "documentManagement",
        url: "/documents",
        display: "Document Management",
        sort: 1.252,
        parent: "systemSettings",
        views: {
            documentManagement: "documentManagement",
        },
        sticky: true,
        deepRedirect: true,
    },
    documents: {
        name: "documents",
        url: "/manage",
        display: "Documents",
        sort: 1.2521,
        parent: "documentManagement",
        views: {
            documents: "documents",
        },
        sticky: true,
        deepRedirect: true,
    },
    documentTemplates: {
        name: "documentTemplates",
        url: "/templates",
        display: "Templates",
        sort: 1.2523,
        parent: "documentManagement",
        views: {
            documentTemplates: "documentTemplates",
        },
        sticky: true,
        deepRedirect: true,
    },
    documentSubreports: {
        name: "documentSubreports",
        url: "/subreports",
        display: "Subreports",
        sort: 1.2522,
        parent: "documentManagement",
        views: {
            documentSubreports: "documentSubreports",
        },
        sticky: true,
        deepRedirect: true,
    },
    servicePortal: {
        name: "servicePortal",
        url: "/service/portal",
        display: "Service Portal",
        sort: 1.26,
        parent: "systemSettings",
        views: {
            servicePortal: "servicePortal",
        },
        sticky: true,
    },
    masterDefinitions: {
        name: "masterDefinitions",
        url: "/master/definitions",
        display: "Master Definitions",
        sort: 1.2601,
        parent: "systemSettings",
        views: {
            masterDefinitions: "masterDefinitions",
        },
        sticky: true,
        deepRedirect: true,
    },
    searchSettings: {
        name: "searchSettings",
        url: "/search",
        display: "Search Settings",
        sort: 1.27,
        parent: "systemSettings",
        views: {
            searchSettings: "searchSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    searchSettingsEvents: {
        name: "searchSettingsEvents",
        url: "/events",
        display: "Events",
        sort: 1.271,
        parent: "searchSettings",
        views: {
            searchSettingsEvents: "searchSettingsEvents",
        },
        sticky: true,
        deepRedirect: true,
    },
    searchSettingsLocations: {
        name: "searchSettingsLocations",
        url: "/locations",
        display: "Locations",
        sort: 1.272,
        parent: "searchSettings",
        views: {
            searchSettingsLocations: "searchSettingsLocations",
        },
        sticky: true,
        deepRedirect: true,
    },
    searchSettingsResources: {
        name: "searchSettingsResources",
        url: "/resources",
        display: "Resources",
        sort: 1.273,
        parent: "searchSettings",
        views: {
            searchSettingsResources: "searchSettingsResources",
        },
        sticky: true,
        deepRedirect: true,
    },
    searchSettingsOrganizations: {
        name: "searchSettingsOrganizations",
        url: "/organizations",
        display: "Organizations",
        sort: 1.274,
        parent: "searchSettings",
        views: {
            searchSettingsOrganizations: "searchSettingsOrganizations",
        },
        sticky: true,
        deepRedirect: true,
    },
    searchSettingsTasks: {
        name: "searchSettingsTasks",
        url: "/tasks",
        display: "Tasks",
        sort: 1.275,
        parent: "searchSettings",
        views: {
            searchSettingsTasks: "searchSettingsTasks",
        },
        sticky: true,
        deepRedirect: true,
    },
    customAttributes: {
        name: "customAttributes",
        url: "/custom/attributes",
        display: "Custom Attributes",
        sort: 1.2602,
        isDefault: true,
        parent: "masterDefinitions",
        views: {
            customAttributes: "customAttributes",
        },
        sticky: true,
        deepRedirect: true,
    },
    eventCustomAttributes: {
        name: "eventCustomAttributes",
        url: "/event",
        display: "Event",
        sort: 1.26021,
        isDefault: true,
        parent: "customAttributes",
        views: {
            eventCustomAttributes: "eventCustomAttributes",
        },
        sticky: true,
    },
    locationCustomAttributes: {
        name: "locationCustomAttributes",
        url: "/location",
        display: "Location",
        sort: 1.26022,
        parent: "customAttributes",
        views: {
            locationCustomAttributes: "locationCustomAttributes",
        },
        sticky: true,
    },
    resourceCustomAttributes: {
        name: "resourceCustomAttributes",
        url: "/resource",
        display: "Resource",
        sort: 1.26023,
        parent: "customAttributes",
        views: {
            resourceCustomAttributes: "resourceCustomAttributes",
        },
        sticky: true,
    },
    organizationCustomAttributes: {
        name: "organizationCustomAttributes",
        url: "/organization",
        display: "Organization",
        sort: 1.26024,
        parent: "customAttributes",
        views: {
            organizationCustomAttributes: "organizationCustomAttributes",
        },
        sticky: true,
    },
    contactCustomAttributes: {
        name: "contactCustomAttributes",
        url: "/contact",
        display: "Contact",
        sort: 1.26025,
        parent: "customAttributes",
        views: {
            contactCustomAttributes: "contactCustomAttributes",
        },
        sticky: true,
    },
    categories: {
        name: "categories",
        url: "/categories",
        display: "Categories",
        sort: 1.2603,
        parent: "masterDefinitions",
        views: {
            categories: "categories",
        },
        sticky: true,
        deepRedirect: true,
    },
    eventCategories: {
        name: "eventCategories",
        url: "/event",
        display: "Event",
        sort: 1.26031,
        isDefault: true,
        parent: "categories",
        views: {
            eventCategories: "eventCategories",
        },
        sticky: true,
    },
    locationCategories: {
        name: "locationCategories",
        url: "/location",
        display: "Location",
        sort: 1.26032,
        parent: "categories",
        views: {
            locationCategories: "locationCategories",
        },
        sticky: true,
    },
    resourceCategories: {
        name: "resourceCategories",
        url: "/resource",
        display: "Resource",
        sort: 1.26033,
        parent: "categories",
        views: {
            resourceCategories: "resourceCategories",
        },
        sticky: true,
    },
    organizationCategories: {
        name: "organizationCategories",
        url: "/organization",
        display: "Organization",
        sort: 1.26034,
        parent: "categories",
        views: {
            organizationCategories: "organizationCategories",
        },
        sticky: true,
    },
    roles: {
        name: "roles",
        url: "/roles",
        display: "Roles",
        sort: 1.2604,
        parent: "masterDefinitions",
        views: {
            roles: "roles",
        },
        sticky: true,
        deepRedirect: true,
    },
    eventRoles: {
        name: "eventRoles",
        url: "/event",
        display: "Event",
        sort: 1.26041,
        isDefault: true,
        parent: "roles",
        views: {
            eventRoles: "eventRoles",
        },
        sticky: true,
    },
    organizationRoles: {
        name: "organizationRoles",
        url: "/organization",
        display: "Organization",
        sort: 1.26042,
        parent: "roles",
        views: {
            organizationRoles: "organizationRoles",
        },
        sticky: true,
    },
    requirements: {
        name: "requirements",
        url: "/requirements",
        display: "Requirements",
        sort: 1.2605,
        parent: "masterDefinitions",
        views: {
            requirements: "requirements",
        },
        sticky: true,
        deepRedirect: true,
    },
    otherRequirements: {
        name: "otherRequirements",
        url: "/other",
        display: "Other",
        sort: 1.26051,
        isDefault: true,
        parent: "requirements",
        views: {
            otherRequirements: "otherRequirements",
        },
        sticky: true,
    },
    calendarRequirements: {
        name: "calendarRequirements",
        url: "/calendar",
        display: "Calendar",
        sort: 1.26052,
        parent: "requirements",
        views: {
            calendarRequirements: "calendarRequirements",
        },
        sticky: true,
    },
    features: {
        name: "features",
        url: "/features",
        display: "Features",
        sort: 1.2606,
        parent: "masterDefinitions",
        views: {
            features: "features",
        },
        sticky: true,
    },
    layouts: {
        name: "layouts",
        url: "/layouts",
        display: "Layouts",
        sort: 1.2607,
        parent: "masterDefinitions",
        views: {
            layouts: "layouts",
        },
        sticky: true,
    },
    partitions: {
        name: "partitions",
        url: "/partitions",
        display: "Partitions",
        sort: 1.2608,
        parent: "masterDefinitions",
        views: {
            partitions: "partitions",
        },
        sticky: true,
    },
    orgTypes: {
        name: "orgTypes",
        url: "org/types",
        display: "Org Types",
        sort: 1.2609,
        parent: "masterDefinitions",
        views: {
            orgTypes: "orgTypes",
        },
        sticky: true,
    },
    ratings: {
        name: "ratings",
        url: "/ratings",
        display: "Org Ratings",
        sort: 1.26091,
        parent: "masterDefinitions",
        views: {
            ratings: "ratings",
        },
        sticky: true,
    },
    eventTypes: {
        name: "eventTypes",
        url: "/event/types",
        display: "Event Types",
        sort: 1.260912,
        parent: "masterDefinitions",
        views: {
            eventTypes: "eventTypes",
        },
        sticky: true,
        deepRedirect: true,
    },
    eventTypeList: {
        name: "eventTypeList",
        url: "/list",
        display: "List",
        sort: 1.2609121,
        parent: "eventTypes",
        views: {
            eventTypeList: "eventTypeList",
        },
        sticky: true,
    },
    eventTypeHierarchy: {
        name: "eventTypeHierarchy",
        url: "/hierarchy/:date/:form/:itemTypeId/:mode/:type/:typeId",
        display: "Hierarchy",
        sort: 1.2609122,
        parent: "eventTypes",
        params: {
            date: {
                value: "",
                squash: true,
                dynamic: true,
            },
            form: {
                value: "form",
                squash: true,
                dynamic: false,
            },
            itemTypeId: {
                value: "",
                squash: true,
                dynamic: true,
            },
            mode: {
                value: "",
                squash: true,
                dynamic: true,
            },
            type: {
                value: "",
                squash: true,
                dynamic: true,
            },
            typeId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        views: {
            eventTypeHierarchy: "eventTypeHierarchy",
        },
        sticky: true,
    },
    images: {
        name: "images",
        url: "/images",
        display: "Images",
        sort: 1.261,
        parent: "masterDefinitions",
        views: {
            images: "images",
        },
        sticky: true,
    },
    cabinetFolderAdvForm: {
        name: "cabinetFolderAdvForm",
        url: "/form/:itemTypeId/:mode/:type/:typeId",
        display: "Hierarchy",
        sort: 1.2609123,
        parent: "eventTypes",
        params: {
            itemTypeId: {
                value: "",
                squash: true,
                dynamic: true,
            },
            mode: {
                value: "",
                squash: true,
                dynamic: true,
            },
            type: {
                value: "",
                squash: true,
                dynamic: true,
            },
            typeId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        views: {
            cabinetFolderAdvForm: "cabinetFolderAdvForm",
        },
        sticky: false,
        isHidden: true,
    },
    pricingSettings: {
        name: "pricingSettings",
        url: "/pricing/settings",
        display: "Pricing Settings",
        sort: 1.2801,
        parent: "systemSettings",
        views: {
            pricingSettings: "pricingSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    pricingFormulas: {
        name: "pricingFormulas",
        url: "/formulas",
        display: "Rate Schedules",
        sort: 1.2802,
        parent: "pricingSettings",
        views: {
            pricingFormulas: "pricingFormulas",
        },
        sticky: true,
        deepRedirect: true,
    },
    editPricingFormulas: {
        name: "editPricingFormulas",
        url: "/edit",
        display: "Edit Schedules",
        sort: 1.28021,
        parent: "pricingFormulas",
        views: {
            editPricingFormulas: "editPricingFormulas",
        },
        sticky: true,
    },
    createPricingFormulas: {
        name: "createPricingFormulas",
        url: "/create",
        display: "Create Schedule",
        sort: 1.28022,
        parent: "pricingFormulas",
        views: {
            createPricingFormulas: "createPricingFormulas",
        },
        sticky: true,
    },
    pricingSetups: {
        name: "pricingSetups",
        url: "/setups",
        display: "Pricing Set-Ups",
        sort: 1.2803,
        parent: "pricingSettings",
        views: {
            pricingSetups: "pricingSetups",
        },
        sticky: true,
        deepRedirect: true,
    },
    rateGroups: {
        name: "rateGroups",
        url: "/rate/groups",
        display: "Rate Groups",
        sort: 1.28031,
        parent: "pricingSetups",
        views: {
            rateGroups: "rateGroups",
        },
        sticky: true,
    },
    taxSchedules: {
        name: "taxSchedules",
        url: "/tax/schedules",
        display: "Tax Schedules",
        sort: 1.28032,
        parent: "pricingSetups",
        views: {
            taxSchedules: "taxSchedules",
        },
        sticky: true,
    },
    pricingTimes: {
        name: "pricingTimes",
        url: "/times",
        display: "Pricing Times",
        sort: 1.28033,
        parent: "pricingSetups",
        views: {
            pricingTimes: "pricingTimes",
        },
        sticky: true,
    },
    accountingFormats: {
        name: "accountingFormats",
        url: "/accounting/formats",
        display: "Account Formats",
        sort: 1.2804,
        parent: "pricingSettings",
        views: {
            accountingFormats: "accountingFormats",
        },
        sticky: true,
    },
    publisherSettings: {
        name: "publisherSettings",
        url: "/publisher/settings",
        display: "Publisher Settings",
        sort: 1.29,
        parent: "systemSettings",
        views: {
            publisherSettings: "publisherSettings",
        },
        sticky: true,
    },
    optimizerEffectiveDating: {
        name: "optimizerEffectiveDating",
        url: "/optimizer/effective/dating",
        display: "Optimizer Effective Dating",
        sort: 6.21,
        parent: "home",
        views: {
            optimizerEffectiveDating: "optimizerEffectiveDating",
        },
        sticky: true,
    },
    userSettings: {
        name: "userSettings",
        url: "/user/settings",
        params: {},
        display: "User Settings",
        sort: 1.3,
        parent: "home",
        views: {
            userSettings: "userSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    editContactInfo: {
        name: "editContactInfo",
        url: "/editContactInfo",
        display: "Edit Contact Info",
        sort: 1.31,
        parent: "userSettings",
        views: {
            editContactInfo: "editContactInfo",
        },
        sticky: true,
        isDefault: true,
    },
    changePassword: {
        name: "changePassword",
        url: "/changePassword",
        display: "Change Password",
        sort: 1.32,
        parent: "userSettings",
        views: {
            changePassword: "changePassword",
        },
        sticky: true,
    },
    dateTimePreferences: {
        name: "dateTimePreferences",
        url: "/dateTimePreferences",
        display: "Display",
        sort: 1.33,
        parent: "userSettings",
        views: {
            dateTimePreferences: "dateTimePreferences",
        },
        sticky: true,
    },
    workflowSettings: {
        name: "workflowSettings",
        url: "/workflow/settings",
        display: "Workflow Settings",
        sort: 1.29,
        parent: "systemSettings",
        views: {
            workflowSettings: "workflowSettings",
        },
        sticky: true,
    },
    masterDefinitionTags: {
        name: "masterDefinitionTags",
        url: "/tags",
        display: "System Tags",
        sort: 1.3,
        parent: "systemSettings",
        views: {
            masterDefinitionTags: "masterDefinitionTags",
        },
        sticky: true,
        deepRedirect: true,
    },
    standardSchedules: {
        name: "standardSchedules",
        url: "/standard/schedules",
        display: "Standard Schedules",
        sort: 1.4,
        parent: "systemSettings",
        views: {
            standardSchedules: "standardSchedules",
        },
        sticky: true,
        deepRedirect: true,
    },
    integrations: {
        name: "integrations",
        url: "/integrations",
        display: "Integrations",
        sort: 1.5,
        parent: "systemSettings",
        views: {
            integrations: "integrations",
        },
        sticky: true,
        deepRedirect: true,
    },
    stripeSettings: {
        name: "stripeSettings",
        url: "/stripeSettings",
        display: "Stripe Settings",
        sort: 1.51,
        parent: "integrations",
        views: {
            stripeSettings: "stripeSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    sevenPointSolutions: {
        name: "sevenPointSolutions",
        url: "/sevenPointSolutions",
        display: "7 Point Solutions Settings",
        sort: 1.52,
        parent: "integrations",
        views: {
            sevenPointSolutions: "sevenPointSolutions",
        },
        sticky: true,
        deepRedirect: true,
    },
    docusignSettings: {
        name: "docusignSettings",
        url: "/docusignSettings",
        display: "DocuSign",
        sort: 1.525,
        parent: "integrations",
        views: {
            docusignSettings: "docusignSettings",
        },
        sticky: true,
        deepRedirect: true,
    },
    legacySis: {
        name: "legacySis",
        url: "/sisImportExport",
        display: "SIS Import/Export",
        sort: 1.53,
        parent: "integrations",
        views: {
            legacySis: "legacySis",
        },
        sticky: true,
        deepRedirect: true,
    },
    housing: {
        name: "housing",
        url: "/housing",
        display: "Housing",
        sort: 1.6,
        parent: "home",
        views: {
            housing: "housing",
        },
        sticky: true,
        deepRedirect: true,
    },
    academicHome: {
        name: "academicHome",
        component: "academicHome",
        url: "/academic",
        display: AcademicLabels.PRODUCT,
        root: true,
        parent: "appRoot",
        sort: 20,
        views: {
            "": "academicHome",
        },
        sticky: true,
    },
    academicSectionScheduling: {
        name: "academicSectionScheduling",
        component: "academicSectionScheduling",
        url: "/section-scheduling",
        display: AcademicLabels.SECTION_SCHEDULING,
        sort: 20.1,
        parent: "academicHome",
        views: {
            academicSectionScheduling: "academicSectionScheduling",
        },
        sticky: true,
    },
    academicInstructorScheduling: {
        name: "academicInstructorScheduling",
        component: "academicInstructorScheduling",
        url: "/instructor-scheduling",
        display: AcademicLabels.INSTRUCTOR_SCHEDULING,
        sort: 20.2,
        parent: "academicHome",
        views: {
            academicInstructorScheduling: "academicInstructorScheduling",
        },
        sticky: true,
    },
    academicScheduleValidation: {
        name: "academicScheduleValidation",
        component: "academicScheduleValidation",
        url: "/schedule-validation",
        display: AcademicLabels.SCHEDULE_VALIDATION,
        sort: 20.3,
        parent: "academicHome",
        views: {
            academicScheduleValidation: "academicScheduleValidation",
        },
        sticky: true,
    },
    academicCurriculum: {
        name: "academicCurriculum",
        component: "academicCurriculum",
        url: "",
        display: AcademicLabels.CURRICULUM,
        sort: 20.4,
        isDefault: true,
        parent: "academicHome",
        views: {
            academicCurriculum: "academicCurriculum",
        },
        sticky: true,
    },
    academicCatalog: {
        name: "academicCatalog",
        component: "academicCatalog",
        url: "/catalog",
        display: AcademicLabels.CATALOG,
        sort: 20.5,
        parent: "academicHome",
        views: {
            academicCatalog: "academicCatalog",
        },
        sticky: true,
    },
    academicRetentionForecast: {
        name: "academicRetentionForecast",
        component: "academicRetentionForecast",
        url: "/retention-forecast",
        display: AcademicLabels.RETENTION_FORECAST,
        sort: 20.6,
        parent: "academicHome",
        views: {
            academicRetentionForecast: "academicRetentionForecast",
        },
        sticky: true,
    },
    matchProfile: {
        name: "matchProfile",
        component: "matchProfile",
        url: "/match/profile/:inviteHash",
        display: "Match Profile",
        sort: 0.02,
        parent: "appRoot",
        views: {
            "": "matchProfile",
        },
        params: {
            inviteHash: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
    },
    buildings: {
        name: "buildings",
        url: "/buildings",
        display: "Buildings",
        sort: 1.262,
        parent: "masterDefinitions",
        views: {
            buildings: "buildings",
        },
        sticky: true,
        deepRedirect: true,
    },
    buildingsSettings: {
        name: "buildingsSettings",
        url: "/settings",
        display: "Settings",
        sort: 1.2621,
        parent: "buildings",
        views: {
            buildingsSettings: "buildingsSettings",
        },
        sticky: false,
    },
    buildingsBulkCreate: {
        name: "buildingsBulkCreate",
        url: "/bulkcreate",
        display: "Bulk Create",
        sort: 1.2622,
        parent: "buildings",
        views: {
            buildingsBulkCreate: "buildingsBulkCreate",
        },
        sticky: false,
    },
    list: {
        name: "list",
        url: "/list",
        display: "List",
        sort: 3,
        parent: "home",
        views: {
            list: "homeList",
        },
        sticky: true,
    },
    calendar: {
        name: "calendar",
        url: "/calendar",
        display: "Calendar",
        sort: 4,
        parent: "home",
        views: {
            calendar: "homeCalendar",
        },
        sticky: true,
    },
    availability: {
        name: "availability",
        url: "/availability",
        display: "Availability",
        sort: 5,
        parent: "home",
        views: {
            availability: "homeAvailability",
        },
        sticky: true,
    },
    search: {
        name: "search",
        url: "/search/:subject/:viz/:search",
        params: {
            viz: {
                value: "",
                squash: true,
                dynamic: true,
            },
            subject: {
                value: "",
                squash: true,
                dynamic: true,
            },
            search: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        display: "Search",
        sort: 6,
        parent: "home",
        views: {
            search: "homeSearch",
        },
        sticky: true,
    },
    grids: {
        name: "grids",
        url: "/grids",
        display: "Meeting Pattern Grids",
        sort: 6.2,
        parent: "home",
        views: {
            grids: "grids",
        },
        sticky: true,
        deepRedirect: true,
    },
    academicGrids: {
        name: "academicGrids",
        url: "/academic",
        display: "Academic Grids",
        sort: 6.21,
        parent: "grids",
        views: {
            academicGrids: "academicGrids",
        },
        sticky: true,
    },
    optimizerGrids: {
        name: "optimizerGrids",
        url: "/optimizer",
        display: "Optimizer Grids",
        sort: 6.22,
        parent: "grids",
        views: {
            optimizerGrids: "homeCoopSched",
        },
        sticky: true,
    },
    publisher: {
        name: "publisher",
        url: "/publisher",
        display: "Publisher",
        sort: 7,
        parent: "home",
        views: {
            publisher: "homePublisher",
        },
        sticky: true,
    },
    event: {
        name: "event",
        url: "/event/{itemId:-*[0-9]+}",
        display: "Event",
        sort: 8,
        parent: "home",
        views: {
            event: "homeEvent",
        },
        sticky: true,
        deepRedirect: true,
        viewPrefName: "25L_event_details_view",
    },
    eventDetails: {
        name: "eventDetails",
        url: "/details",
        display: "Details",
        sort: 1,
        isDefault: true,
        parent: "event",
        views: {
            eventDetails: "eventDetails",
        },
        sticky: true,
    },
    eventOccurrences: {
        name: "eventOccurrences",
        url: "/occurrences",
        display: "Occurrences",
        sort: 2,
        parent: "event",
        views: {
            eventOccurrences: "eventOccurrences",
        },
        sticky: true,
    },
    eventCalendar: {
        name: "eventCalendar",
        url: "/calendar",
        display: "Calendar",
        sort: 3,
        parent: "event",
        views: {
            eventCalendar: "eventCalendar",
        },
        sticky: true,
    },
    eventSchedule: {
        name: "eventSchedule",
        url: "/schedule",
        display: "Schedule",
        sort: 4,
        parent: "event",
        views: {
            eventSchedule: "eventSchedule",
        },
        sticky: true,
    },
    eventTasks: {
        name: "eventTasks",
        url: "/tasks",
        display: "Task List",
        sort: 5,
        parent: "event",
        views: {
            eventTasks: "eventTasks",
        },
        sticky: true,
    },
    eventPricing: {
        name: "eventPricing",
        url: "/pricing/:evBillId",
        display: "Pricing",
        sort: 6,
        parent: "event",
        views: {
            eventPricing: "eventPricing",
        },
        params: {
            evBillId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    eventLynx: {
        name: "eventLynx",
        url: "/lynx",
        display: "Lynx",
        sort: 6.1,
        parent: "event",
        views: {
            eventLynx: "eventLynx",
        },
        sticky: true,
    },
    eventRegistration: {
        name: "eventRegistration",
        url: "/registration",
        display: "Registration",
        sort: 6.2,
        parent: "event",
        views: {
            eventRegistration: "eventRegistration",
        },
        sticky: true,
    },
    eventAudit: {
        name: "eventAudit",
        url: "/audit",
        display: "Audit Trail",
        sort: 7,
        parent: "event",
        views: {
            eventAudit: "eventAudit",
        },
        sticky: true,
    },
    eventEmails: {
        name: "eventEmails",
        url: "/scheduled/emails",
        display: "Scheduled Emails",
        sort: 8,
        parent: "event",
        views: {
            eventEmails: "eventEmails",
        },
        sticky: true,
    },
    eventEmailHistory: {
        name: "eventEmailHistory",
        url: "/email/history",
        display: "Threaded Emails",
        sort: 8.5,
        parent: "event",
        views: {
            eventEmailHistory: "eventEmailHistory",
        },
        sticky: true,
    },
    location: {
        name: "location",
        url: "/location/{itemId:-*[0-9]+}",
        display: "Location",
        sort: 9,
        parent: "home",
        views: {
            location: "homeLocation",
        },
        sticky: true,
        deepRedirect: true,
        viewPrefName: "25L_space_details_view",
    },
    locationDetails: {
        name: "locationDetails",
        url: "/details",
        display: "Details",
        sort: 1,
        isDefault: true,
        parent: "location",
        views: {
            locationDetails: "locationDetails",
        },
        sticky: true,
    },
    locationList: {
        name: "locationList",
        url: "/list",
        display: "List",
        sort: 2,
        parent: "location",
        views: {
            locationList: "locationList",
        },
        sticky: true,
    },
    locationAvailability: {
        name: "locationAvailability",
        url: "/availability/daily",
        display: "Availability (Daily)",
        sort: 3,
        parent: "location",
        views: {
            locationAvailability: "locationAvailability",
        },
        params: {
            startDt: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    locationAvailabilityWeekly: {
        name: "locationAvailabilityWeekly",
        url: "/availability/weekly/:startDt/:weeks",
        display: "Availability (Weekly)",
        sort: 4,
        parent: "location",
        params: {
            startDt: {
                value: "",
                squash: true,
                dynamic: true,
            },
            weeks: {
                value: "",
                squash: true,
                dynamic: false,
            },
            dows: {
                value: "",
                squash: true,
                dynamic: false,
            },
        },
        views: {
            locationAvailabilityWeekly: "locationAvailabilityWeekly",
        },
        sticky: true,
    },
    locationCalendar: {
        name: "locationCalendar",
        url: "/calendar",
        display: "Calendar",
        sort: 5,
        parent: "location",
        views: {
            locationCalendar: "locationCalendar",
        },
        sticky: true,
    },
    locationAudit: {
        name: "locationAudit",
        url: "/audit",
        display: "Audit Trail",
        sort: 7,
        parent: "location",
        views: {
            locationAudit: "locationAudit",
        },
        sticky: true,
    },

    locationWaitlist: {
        name: "locationWaitlist",
        url: "/waitlist",
        display: "Waitlist",
        sort: 8,
        parent: "location",
        params: {
            startDt: {
                value: "",
                squash: true,
                dynamic: true,
            },
            weeks: {
                value: "",
                squash: true,
                dynamic: false,
            },
            dows: {
                value: "",
                squash: true,
                dynamic: false,
            },
        },
        views: {
            locationWaitlist: "locationWaitlist",
        },
        sticky: true,
    },
    resource: {
        name: "resource",
        url: "/resource/{itemId:-*[0-9]+}",
        display: "Resource",
        sort: 10,
        parent: "home",
        views: {
            resource: "homeResource",
        },
        sticky: true,
        deepRedirect: true,
        viewPrefName: "25L_resource_details_view",
    },
    resourceDetails: {
        name: "resourceDetails",
        url: "/details",
        display: "Details",
        sort: 1,
        isDefault: true,
        parent: "resource",
        views: {
            resourceDetails: "resourceDetails",
        },
        sticky: true,
    },
    resourceList: {
        name: "resourceList",
        url: "/list",
        display: "List",
        sort: 2,
        parent: "resource",
        views: {
            resourceList: "resourceList",
        },
        sticky: true,
    },
    resourceAvailability: {
        name: "resourceAvailability",
        url: "/availability/daily",
        display: "Availability (Daily)",
        sort: 3,
        parent: "resource",
        views: {
            resourceAvailability: "resourceAvailability",
        },
        sticky: true,
    },
    resourceCalendar: {
        name: "resourceCalendar",
        url: "/calendar",
        display: "Calendar",
        sort: 4,
        parent: "resource",
        views: {
            resourceCalendar: "resourceCalendar",
        },
        sticky: true,
    },
    resourceAudit: {
        name: "resourceAudit",
        url: "/audit",
        display: "Audit Trail",
        sort: 7,
        parent: "resource",
        views: {
            resourceAudit: "resourceAudit",
        },
        sticky: true,
    },
    organization: {
        name: "organization",
        url: "/organization/{itemId:-*[0-9]+}",
        display: "Organization",
        sort: 11,
        parent: "home",
        views: {
            organization: "homeOrganization",
        },
        sticky: true,
        deepRedirect: true,
        viewPrefName: "25L_org_details_view",
    },
    organizationDetails: {
        name: "organizationDetails",
        url: "/details",
        display: "Details",
        sort: 1,
        isDefault: true,
        parent: "organization",
        views: {
            organizationDetails: "organizationDetails",
        },
        sticky: true,
    },
    organizationList: {
        name: "organizationList",
        url: "/list",
        display: "List",
        sort: 2,
        parent: "organization",
        views: {
            organizationList: "organizationList",
        },
        sticky: true,
    },
    organizationCalendar: {
        name: "organizationCalendar",
        url: "/calendar",
        display: "Calendar",
        sort: 3,
        parent: "organization",
        views: {
            organizationCalendar: "organizationCalendar",
        },
        sticky: true,
    },
    organizationInvoices: {
        name: "organizationInvoices",
        url: "/invoices",
        display: "Invoices",
        sort: 4,
        parent: "organization",
        views: {
            organizationInvoices: "organizationInvoices",
        },
        sticky: true,
    },
    organizationAudit: {
        name: "organizationAudit",
        url: "/audit",
        display: "Audit Trail",
        sort: 7,
        parent: "organization",
        views: {
            organizationAudit: "organizationAudit",
        },
        sticky: true,
    },
    contact: {
        name: "contact",
        url: "/contact/{itemId:-*[0-9]+}",
        display: "Contact",
        sort: 13,
        parent: "home",
        views: {
            contact: "homeContact",
        },
        sticky: true,
        deepRedirect: true,
        //to-do sort out pref stuff
        //viewPrefName: "25L_cont_details_view"
    },
    contactDetails: {
        name: "contactDetails",
        url: "/details",
        display: "Details",
        sort: 1,
        isDefault: true,
        parent: "contact",
        views: {
            contactDetails: "contactDetails",
        },
        sticky: true,
    },
    contactList: {
        name: "contactList",
        url: "/list",
        display: "List",
        sort: 2,
        parent: "contact",
        views: {
            contactList: "contactList",
        },
        sticky: true,
    },
    contactCalendar: {
        name: "contactCalendar",
        url: "/calendar",
        display: "Calendar",
        sort: 3,
        parent: "contact",
        views: {
            contactCalendar: "contactCalendar",
        },
        sticky: true,
    },
    contactAudit: {
        name: "contactAudit",
        url: "/audit",
        display: "Audit Trail",
        sort: 7,
        parent: "contact",
        views: {
            contactAudit: "contactAudit",
        },
        sticky: true,
    },
    report: {
        name: "report",
        display: "Reports",
        parent: "external",
        url: S25Util.getURL("", "/reports"),
        views: {},
    },
    embeddingsTest: {
        name: "embeddingsTest",
        url: "/embeddings-test",
        display: "Embeddings Test",
        sort: 16,
        parent: "home",
        views: {
            embeddingsTest: "embeddingsTest",
        },
        sticky: true,
    },
    importTool: {
        name: "importTool",
        url: "/importTool",
        display: "Import Tool",
        sort: 18,
        parent: "home",
        views: {
            importTool: "importTool",
        },
        sticky: true,
    },
    seHome: {
        name: "seHome",
        component: "seHome",
        url: "/connect25",
        display: "Connect25",
        root: true,
        parent: "appRoot",
        sort: 19,
        views: {
            "": "seHome",
        },
        sticky: true,
    },
    seLogin: {
        name: "seLogin",
        component: "seLogin",
        url: "/login",
        display: "Connect25 Login",
        parent: "seHome",
        sort: 19.01,
        views: {
            seLogin: "seLogin",
        },
    },
    sePasswordReset: {
        name: "sePasswordReset",
        url: "/password_reset?pw_reset_token",
        parent: "seHome",
        isHidden: true,
        views: {
            sePasswordReset: "sePasswordReset",
        },
    },
    seHomeView: {
        name: "seHomeView",
        component: "seHomeView",
        url: "",
        display: "Home",
        sort: 19.1,
        isDefault: true,
        parent: "seHome",
        views: {
            seHomeView: "seHomeView",
        },
        sticky: true,
    },
    seChat: {
        name: "seChat",
        component: "seChat",
        // Same deep-link contract as Pro chat — see chat state comment (reload on search change).
        url: "/chat?externalId&channelType&socChannelId&title&externalType",
        display: "Chat",
        sort: 19.2,
        parent: "seHome",
        views: {
            seChat: "seChat",
        },
        sticky: true,
    },
    seEmail: {
        name: "seEmail",
        component: "seEmail",
        url: "/email",
        display: "Compose",
        sort: 19.25,
        parent: "seHome",
        views: {
            seEmail: "seEmail",
        },
        sticky: true,
    },
    seEmailSent: {
        name: "seEmailSent",
        component: "seEmailSent",
        url: "/email/sent",
        display: "Sent",
        sort: 19.26,
        parent: "seHome",
        views: {
            seEmailSent: "seEmailSent",
        },
        sticky: true,
    },
    seForms: {
        name: "seForms",
        component: "seForms",
        url: "/forms/:ingestionPointId?",
        display: "Forms",
        sort: 19.3,
        parent: "seHome",
        views: {
            seForms: "seForms",
        },
        params: {
            ingestionPointId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        onActive: () => {
            import("../../modules/data-ingestion/admin/ingestion.admin.navigation").then((nav) =>
                nav.notifySeFormsActivated(),
            );
        },
        sticky: true,
    },
    seSubmissionSearch: {
        name: "seSubmissionSearch",
        component: "seSubmissionSearch",
        url: "/submission-search",
        display: "Submission Search",
        sort: 19.31,
        parent: "seHome",
        views: {
            seSubmissionSearch: "seSubmissionSearch",
        },
        sticky: true,
    },
    seSubmissionAggregations: {
        name: "seSubmissionAggregations",
        component: "seSubmissionAggregations",
        url: "/submission-aggregations",
        display: "Submission Aggregations",
        sort: 19.32,
        parent: "seHome",
        views: {
            seSubmissionAggregations: "seSubmissionAggregations",
        },
        sticky: true,
    },
    seMyForms: {
        name: "seMyForms",
        component: "seMyForms",
        url: "/myForms",
        display: "My Forms",
        sort: 19.4,
        parent: "seHome",
        views: {
            seMyForms: "seMyForms",
        },
        sticky: true,
    },
    seMyWorkflow: {
        name: "seMyWorkflow",
        component: "seMyWorkflow",
        url: "/myWorkflow/:submissionId",
        display: "My Workflow",
        sort: 19.5,
        parent: "seHome",
        views: {
            seMyWorkflow: "seMyWorkflow",
        },
        params: {
            submissionId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    seMyOrgs: {
        name: "seMyOrgs",
        component: "seMyOrgs",
        url: "/myOrgs",
        display: "My Organizations",
        sort: 19.6,
        parent: "seHome",
        views: {
            seMyOrgs: "seMyOrgs",
        },
        sticky: true,
    },
    seMyEvents: {
        name: "seMyEvents",
        component: "seMyEvents",
        url: "/myEvents",
        display: "My Events",
        sort: 19.7,
        parent: "seHome",
        views: {
            seMyEvents: "seMyEvents",
        },
        sticky: true,
    },
    seMyHours: {
        name: "seMyHours",
        component: "seMyHours",
        url: "/myHours",
        display: "My Service Hours",
        sort: 19.8,
        parent: "seHome",
        views: {
            seMyHours: "seMyHours",
        },
        params: {
            itemId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    seAdminServiceHours: {
        name: "seAdminServiceHours",
        component: "seAdminServiceHours",
        url: "/admin/serviceHours",
        display: "Service Hours",
        sort: 19.85,
        parent: "seHome",
        views: {
            seAdminServiceHours: "seAdminServiceHours",
        },
        sticky: true,
    },
    seElectionBallot: {
        name: "seElectionBallot",
        component: "seElectionBallot",
        url: "/elections/ballot/:electionId",
        display: "Vote",
        sort: 19.75,
        parent: "seHome",
        views: {
            seElectionBallot: "seElectionBallot",
        },
        params: {
            electionId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    seElectionNominations: {
        name: "seElectionNominations",
        component: "seElectionNominations",
        url: "/elections/nominations",
        display: "Election Nominations",
        sort: 19.76,
        parent: "seHome",
        views: {
            seElectionNominations: "seElectionNominations",
        },
        sticky: true,
    },
    seElectionAdmin: {
        name: "seElectionAdmin",
        component: "seElectionAdmin",
        url: "/elections/admin",
        display: "Elections Admin",
        sort: 19.77,
        parent: "seHome",
        views: {
            seElectionAdmin: "seElectionAdmin",
        },
        sticky: true,
    },
    seElectionManualCandidate: {
        name: "seElectionManualCandidate",
        component: "seElectionManualCandidate",
        url: "/elections/admin/candidate",
        display: "Election Candidate",
        sort: 19.771,
        parent: "seHome",
        views: {
            seElectionManualCandidate: "seElectionManualCandidate",
        },
        params: {
            electionId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    seElectionResults: {
        name: "seElectionResults",
        component: "seElectionResults",
        url: "/elections/admin/results",
        display: "Election Results",
        sort: 19.772,
        parent: "seHome",
        views: {
            seElectionResults: "seElectionResults",
        },
        params: {
            electionId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    seWorkflow: {
        name: "seWorkflow",
        component: "seWorkflow",
        url: "/workflow/:workflowProcessId?",
        display: "Workflow",
        sort: 19.9,
        parent: "seHome",
        views: {
            seWorkflow: "seWorkflow",
        },
        params: {
            workflowProcessId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    seWorkflowRules: {
        name: "seWorkflowRules",
        component: "seWorkflowRules",
        url: "/workflowRules",
        display: "Workflow Rules",
        sort: 19.91,
        parent: "seHome",
        views: {
            seWorkflowRules: "seWorkflowRules",
        },
        sticky: true,
    },
    seWorkflowActionHistory: {
        name: "seWorkflowActionHistory",
        component: "seWorkflowActionHistory",
        url: "/workflowActionHistory",
        display: "Workflow Action History",
        sort: 19.92,
        parent: "seHome",
        views: {
            seWorkflowActionHistory: "seWorkflowActionHistory",
        },
        sticky: true,
    },
    seWorkflowSubmissionAudit: {
        name: "seWorkflowSubmissionAudit",
        component: "seWorkflowSubmissionAudit",
        url: "/workflowSubmissionAudit",
        display: "Submission Workflow Audit",
        sort: 19.925,
        parent: "seHome",
        views: {
            seWorkflowSubmissionAudit: "seWorkflowSubmissionAudit",
        },
        sticky: true,
    },
    seBudget: {
        name: "seBudget",
        component: "seBudget",
        url: "/budget",
        display: "Budget",
        sort: 19.94,
        parent: "seHome",
        views: {
            seBudget: "seBudget",
        },
        sticky: true,
    },
    seBudgetCategories: {
        name: "seBudgetCategories",
        component: "seBudgetCategories",
        url: "/budgetCategories",
        display: BudgetLabels.BUDGET_CATEGORIES,
        sort: 19.96,
        parent: "seHome",
        views: {
            seBudgetCategories: "seBudgetCategories",
        },
        sticky: true,
    },
    seFiscalYears: {
        name: "seFiscalYears",
        component: "seFiscalYears",
        url: "/fiscalYears",
        display: "Fiscal Years",
        sort: 19.97,
        parent: "seHome",
        views: {
            seFiscalYears: "seFiscalYears",
        },
        sticky: true,
    },
    sePendingBudgetRequests: {
        name: "sePendingBudgetRequests",
        component: "sePendingBudgetRequests",
        url: "/pendingBudgetRequests",
        display: "Pending Budget Requests",
        sort: 19.98,
        parent: "seHome",
        views: {
            sePendingBudgetRequests: "sePendingBudgetRequests",
        },
        sticky: true,
    },
    seBudgetReports: {
        name: "seBudgetReports",
        component: "seBudgetReports",
        // Search params must be in the url for reloadOnSearch:false to treat them as dynamic (see seEngagementAnalytics).
        url: "/budget/reports?fy&measure&orgs",
        display: BudgetLabels.BUDGET_REPORTS,
        sort: 19.99,
        parent: "seHome",
        views: {
            seBudgetReports: "seBudgetReports",
        },
        sticky: true,
        reloadOnSearch: false,
    },
    seGroups: {
        name: "seGroups",
        component: "seGroups",
        url: "/groups",
        display: "Approval Groups",
        sort: 19.96,
        parent: "seHome",
        views: {
            seGroups: "seGroups",
        },
        sticky: true,
    },
    seUserSettings: {
        name: "seUserSettings",
        component: "seUserSettings",
        url: "/settings",
        display: "User Settings",
        sort: 19.98,
        parent: "seHome",
        views: {
            seUserSettings: "seUserSettings",
        },
        sticky: true,
    },
    studentEngagementOrganization: {
        name: "studentEngagementOrganization",
        component: "studentEngagementOrganization",
        url: "/organization/:itemId",
        display: "Organization",
        sort: 19.3,
        parent: "seHome",
        views: {
            studentEngagementOrganization: "studentEngagementOrganization",
        },
        params: {
            itemId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    seOrganizationDetails: {
        name: "seOrganizationDetails",
        component: "seOrganizationDetails",
        url: "/details",
        display: "Organization",
        sort: 19.31,
        views: {
            seOrganizationDetails: "seOrganizationDetails",
        },
        parent: "studentEngagementOrganization",
        params: {
            itemId: {
                value: "",
                squash: true,
                dynamic: true,
            },
            tab: {
                value: null,
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    seOrganizationHours: {
        name: "seOrganizationHours",
        component: "seOrganizationHours",
        url: "/hours",
        display: "Organization",
        sort: 19.32,
        views: {
            seOrganizationHours: "seOrganizationHours",
        },
        parent: "studentEngagementOrganization",
        params: {
            itemId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    seOrganizations: {
        name: "seOrganizations",
        component: "seOrganizations",
        url: "/organizations",
        display: "Organizations",
        parent: "seHome",
        views: {
            seOrganizations: "seOrganizations",
        },
        sticky: true,
    },
    seOrganizationsSearch: {
        name: "seOrganizationsSearch",
        component: "seOrganizationsSearch",
        url: "/search",
        display: "Organization Search",
        parent: "seOrganizations",
        views: {
            seOrganizationsSearch: "seOrganizationsSearch",
        },
        sticky: true,
    },
    seEvents: {
        name: "seEvents",
        component: "seEvents",
        url: "/events",
        display: "Events",
        parent: "seHome",
        views: {
            seEvents: "seEvents",
        },
        sticky: true,
    },
    seEventsSearch: {
        name: "seEventsSearch",
        component: "seEventsSearch",
        url: "/search",
        display: "Event Search",
        parent: "seEvents",
        views: {
            seEventsSearch: "seEventsSearch",
        },
        sticky: true,
    },
    seUsers: {
        name: "seUsers",
        component: "seUsers",
        url: "/users",
        display: "Users",
        parent: "seHome",
        views: {
            seUsers: "seUsers",
        },
        sticky: true,
    },
    seUsersSearch: {
        name: "seUsersSearch",
        component: "seUsersSearch",
        url: "/search",
        display: "User Search",
        parent: "seUsers",
        views: {
            seUsersSearch: "seUsersSearch",
        },
        sticky: true,
    },
    studentEngagementEvent: {
        name: "studentEngagementEvent",
        component: "studentEngagementEvent",
        url: "/event/:itemId",
        display: "Event",
        sort: 20.2,
        parent: "seHome",
        views: {
            studentEngagementEvent: "studentEngagementEvent",
        },
        params: {
            itemId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
        deepRedirect: true,
    },
    studentEngagementEventDetails: {
        name: "studentEngagementEventDetails",
        component: "studentEngagementEventDetails",
        url: "/details",
        display: "Event",
        sort: 19.41,
        parent: "studentEngagementEvent",
        views: {
            studentEngagementEventDetails: "studentEngagementEventDetails",
        },
        params: {
            itemId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    studentEngagementEventCheckInDash: {
        name: "studentEngagementEventCheckInDash",
        component: "studentEngagementEventCheckInDash",
        url: "/{rsrvId:-?[0-9]+}/checkin",
        display: "Check-In",
        sort: 19.42,
        parent: "studentEngagementEvent",
        views: {
            studentEngagementEventCheckInDash: "studentEngagementEventCheckInDash",
        },
        sticky: true,
    },
    studentEngagementConfig: {
        name: "studentEngagementConfig",
        component: "studentEngagementConfig",
        url: "/admin/config",
        display: "Configuration",
        sort: 20.3,
        parent: "seHome",
        views: {
            studentEngagementConfig: "studentEngagementConfig",
        },
        deepRedirect: true,
        sticky: true,
    },
    seConnect25ConfigAlerts: {
        name: "seConnect25ConfigAlerts",
        component: "seConnect25ConfigAlerts",
        url: "/alerts",
        display: "Alerts",
        parent: "studentEngagementConfig",
        views: {
            seConnect25ConfigAlerts: "seConnect25ConfigAlerts",
        },
        isDefault: true,
        sticky: true,
    },
    seConnect25ConfigAppearance: {
        name: "seConnect25ConfigAppearance",
        component: "seConnect25ConfigAppearance",
        url: "/appearance",
        display: "Appearance",
        parent: "studentEngagementConfig",
        views: {
            seConnect25ConfigAppearance: "seConnect25ConfigAppearance",
        },
        sticky: true,
    },
    seConnect25ConfigAuthentication: {
        name: "seConnect25ConfigAuthentication",
        component: "seConnect25ConfigAuthentication",
        url: "/authentication",
        display: "Authentication",
        parent: "studentEngagementConfig",
        views: {
            seConnect25ConfigAuthentication: "seConnect25ConfigAuthentication",
        },
        sticky: true,
    },
    seConnect25ConfigImport: {
        name: "seConnect25ConfigImport",
        component: "seConnect25ConfigImport",
        url: "/import",
        display: "Import Tool",
        parent: "studentEngagementConfig",
        views: {
            seConnect25ConfigImport: "seConnect25ConfigImport",
        },
        sticky: true,
    },
    seConnect25ConfigInit: {
        name: "seConnect25ConfigInit",
        component: "seConnect25ConfigInit",
        url: "/initialization",
        display: "Initialization",
        parent: "studentEngagementConfig",
        views: {
            seConnect25ConfigInit: "seConnect25ConfigInit",
        },
        sticky: true,
    },
    seConnect25ConfigOrgStatus: {
        name: "seConnect25ConfigOrgStatus",
        component: "seConnect25ConfigOrgStatus",
        url: "/org-status",
        display: "Organization Status Ratings",
        parent: "studentEngagementConfig",
        views: {
            seConnect25ConfigOrgStatus: "seConnect25ConfigOrgStatus",
        },
        sticky: true,
    },
    seEmailTemplateSettings: {
        name: "seEmailTemplateSettings",
        component: "seEmailTemplateSettings",
        url: "/admin/email-template-settings/:emailTemplateId?",
        display: "Email Template Settings",
        sort: 20.35,
        parent: "seHome",
        views: {
            seEmailTemplateSettings: "seEmailTemplateSettings",
        },
        params: {
            emailTemplateId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    studentEngagementSecurity: {
        name: "studentEngagementSecurity",
        component: "studentEngagementSecurity",
        url: "/admin/security",
        display: "Security",
        sort: 20.4,
        parent: "seHome",
        views: {
            studentEngagementSecurity: "studentEngagementSecurity",
        },
        deepRedirect: true,
        sticky: true,
    },
    seMyActivity: {
        name: "seMyActivity",
        component: "seMyActivity",
        url: "/my-activity",
        display: "My Activity",
        sort: 19.81,
        parent: "seHome",
        views: {
            seMyActivity: "seMyActivity",
        },
        sticky: true,
    },
    seMyTrails: {
        name: "seMyTrails",
        component: "seMyTrails",
        url: "/my-trails?defnId",
        display: "My Pathways",
        sort: 19.815,
        parent: "seHome",
        views: {
            seMyTrails: "seMyTrails",
        },
        sticky: true,
        reloadOnSearch: false,
    },
    seEngagementDashboard: {
        name: "seEngagementDashboard",
        component: "seEngagementDashboard",
        url: "/engagement",
        display: "Engagement Reports",
        sort: 20.5,
        parent: "seHome",
        views: {
            seEngagementDashboard: "seEngagementDashboard",
        },
        sticky: true,
    },
    seEngagementAnalytics: {
        name: "seEngagementAnalytics",
        component: "seEngagementAnalytics",
        // The dashboard writes its filter to the query string for shareable deep links (one-way). The
        // search params MUST be declared here: ui-router only treats reloadOnSearch:false params as
        // `dynamic` (update without reloading the state) when they are part of the state url. Without
        // both pieces, mutating the query string reloads/remounts the view and loops forever.
        url: "/analytics?dateStart&dateEnd&bucket&metric&excludeNone&ca&orgs&cats&events&locs",
        display: "Engagement Analytics",
        sort: 20.6,
        parent: "seHome",
        views: {
            seEngagementAnalytics: "seEngagementAnalytics",
        },
        sticky: true,
        reloadOnSearch: false,
    },
    seEngagementConfig: {
        name: "seEngagementConfig",
        component: "seEngagementConfig",
        url: "/engagement/config",
        display: "Engagement Configuration",
        sort: 20.6,
        parent: "seHome",
        views: {
            seEngagementConfig: "seEngagementConfig",
        },
        sticky: true,
    },
    seBadgeConfig: {
        name: "seBadgeConfig",
        component: "seBadgeConfig",
        url: "/engagement/badges",
        display: "Badge Configuration",
        sort: 20.7,
        parent: "seHome",
        views: {
            seBadgeConfig: "seBadgeConfig",
        },
        sticky: true,
    },
    seTrailAdmin: {
        name: "seTrailAdmin",
        component: "seTrailAdmin",
        url: "/engagement/trails",
        display: "Curricula",
        sort: 20.75,
        parent: "seHome",
        views: {
            seTrailAdmin: "seTrailAdmin",
        },
        sticky: true,
    },
    seTrailExperiences: {
        name: "seTrailExperiences",
        component: "seTrailExperiences",
        url: "/engagement/trail-experiences",
        display: "Experience Reviews",
        sort: 20.76,
        parent: "seHome",
        views: {
            seTrailExperiences: "seTrailExperiences",
        },
        sticky: true,
    },
    seTrailAdminProgress: {
        name: "seTrailAdminProgress",
        component: "seTrailAdminProgress",
        url: "/engagement/trail-admin-progress?defnId&contId&contactName",
        display: "Student Progress",
        sort: 20.77,
        parent: "seHome",
        views: {
            seTrailAdminProgress: "seTrailAdminProgress",
        },
        sticky: true,
        reloadOnSearch: false,
    },
    seAtRisk: {
        name: "seAtRisk",
        component: "seAtRisk",
        url: "/engagement/at-risk",
        display: "At-Risk Students",
        sort: 20.8,
        parent: "seHome",
        views: {
            seAtRisk: "seAtRisk",
        },
        sticky: true,
    },
    seIdCardsConfig: {
        name: "seIdCardsConfig",
        component: "seIdCardsConfig",
        url: "/engagement/id-cards",
        display: "ID Cards",
        sort: 20.9,
        parent: "seHome",
        views: {
            seIdCardsConfig: "seIdCardsConfig",
        },
        sticky: true,
    },
    seLayoutsConfig: {
        name: "seLayoutsConfig",
        component: "seLayoutsConfig",
        url: "/engagement/layouts",
        display: "Layouts",
        sort: 20.91,
        parent: "seHome",
        views: {
            seLayoutsConfig: "seLayoutsConfig",
        },
        sticky: true,
    },
    eventSurvey: {
        name: "eventSurvey",
        component: "eventSurvey",
        url: "/event-survey/:submissionId",
        display: "Event Survey",
        sort: 1.5,
        parent: "appRoot",
        views: {
            "": "eventSurvey",
        },
        params: {
            submissionId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    guestRegistrationForm: {
        name: "guestRegistrationForm",
        component: "guestRegistrationForm",
        url: "/guest-registration-form/:submissionId",
        display: "Guest Registration Form",
        sort: 1.5,
        parent: "appRoot",
        views: {
            "": "guestRegistrationForm",
        },
        params: {
            submissionId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    guestRegistrationConfirm: {
        name: "guestRegistrationConfirm",
        component: "guestRegistrationConfirm",
        url: "/guest-registration-confirm/:token",
        display: "Confirm Registration",
        sort: 1.55,
        parent: "appRoot",
        views: {
            "": "guestRegistrationConfirm",
        },
        params: {
            token: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    formSubmission: {
        name: "formSubmission",
        component: "formSubmission",
        url: "/form-submission/:submissionId",
        display: "Form Submission",
        sort: 1.6,
        parent: "appRoot",
        views: {
            "": "formSubmission",
        },
        params: {
            submissionId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
    checkIn: {
        name: "checkIn",
        component: "checkIn",
        url: "/check-in/:itemId/:rsrvId",
        display: "Check-In",
        sort: 20,
        parent: "appRoot",
        views: {
            "": "checkIn",
        },
        params: {
            itemId: {
                value: "",
                squash: true,
                dynamic: true,
            },
            rsrvId: {
                value: "",
                squash: true,
                dynamic: true,
            },
        },
        sticky: true,
    },
} as const);

export type S25State = (typeof states)[keyof typeof states];

export const StateMap = states;
export namespace S25States {
    export const typed = states;
    export const untyped: { [stateName in Name]: AbstractState } = states;

    export type UntypedState = AbstractState;

    export type Name = keyof typeof states;

    export const names = <{ [stateName in Name]: stateName }>(
        Object.fromEntries(Object.keys(states).map((stateName: Name) => [stateName, stateName]))
    );

    export type ChildStates<ParentName extends Name | "appRoot" | "external"> = {
        [N in Name]: (typeof states)[N]["parent"] extends ParentName ? N : never;
    }[keyof typeof states];

    /**
     * Extracts the child state names and omits the parent state name. Don't specify the second parameter.
     * e.g. ChildNames<"event" | "location"> would look at:
     *  eventDetails: { ...parent: "event", ...}
     *  eventOccurrences: { ...parent: "event", ...}
     *  eventCalendar: { ...parent: "event", ...}
     *  ...
     *  locationDetails: { ...parent: "location", ...}
     *  locationList: { ...parent: "location", ...}
     *  locationAvailability: { ...parent: "location", ...}
     *  ...
     * and yield: "details" | "occurrences" | "calendar" | "list" | "availability" | ...
     */
    export type ChildNames<
        ParentName extends Name | "appRoot" | "external",
        // this is just the way I was able to make the type work lol
        ChildName extends Name = ChildStates<ParentName>,
    > = ChildName extends `${ParentName}${infer Suffix}` ? Uncapitalize<Suffix> : Uncapitalize<ChildName>;

    type ExtractParams<UrlPattern extends string> = UrlPattern extends `${string}/{${infer Param}:${infer Rest}`
        ? Param | ExtractParams<Rest>
        : UrlPattern extends `${string}:${infer Param}/${infer Rest}`
          ? Param | ExtractParams<Rest>
          : UrlPattern extends `${string}:${infer Param}`
            ? Param
            : never;

    type ParentNameForName<N extends Name> = Exclude<(typeof states)[N]["parent"], "appRoot" | "external">;
    type UrlForName<N extends Name> = (typeof states)[N]["url"];
    type AllParamsForName<N extends Name> =
        ParentNameForName<N> extends never
            ? ExtractParams<UrlForName<N>>
            : AllParamsForName<ParentNameForName<N>> | ExtractParams<UrlForName<N>>;

    export type ParamsForName<N extends Name> = { [param in AllParamsForName<N>]: string | number };
}
